import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ReferenceLine,
} from 'recharts';
import RequiredApiNotice from './RequiredApiNotice';

const TOTAL_CRIME_TREND_LABEL = 'Total Crime Trend';

interface CrimeDashboardProps {
  data: any;
  address: any;
}

const CrimeDashboard: React.FC<CrimeDashboardProps> = ({ data, address }) => {
  if (!data || data.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['FBI Crime Data API', 'City Open Data Crime APIs']} />
      </div>
    );
  }
  const missingFields = [];
  if (data.totalCrimeIndex == null) missingFields.push('Total Crime Index');
  if (data.violentCrimeIndex == null) missingFields.push('Violent Crime Index');
  if (data.propertyCrimeIndex == null) missingFields.push('Property Crime Index');

  const neighborhoodLabel =
    data.neighborhoodName || address?.neighborhood || 'Neighborhood';
  const metroLabel =
    data.metroAreaName ||
    address?.metroArea ||
    address?.msa ||
    'Metro Area';

  const safetyColor =
    data.safetyScore != null
      ? data.safetyScore >= 70
        ? '#28a745'
        : data.safetyScore >= 50
        ? '#ffc107'
        : '#dc3545'
      : '#666';

  const cp = data.crimesPerCapita || {};
  const stateCp = data.stateCrimesPerCapita || {
    murder: data.fbiEstimates?.murder,
    rape: data.fbiEstimates?.rape,
    robbery: data.fbiEstimates?.robbery,
    assault: data.fbiEstimates?.aggravatedAssault,
    burglary: data.fbiEstimates?.burglary,
    larceny: data.fbiEstimates?.larceny,
    vehicleTheft: data.fbiEstimates?.motorVehicleTheft,
  };
  const cd = data.crimeData || {};
  const totalIdx = Number.isFinite(data.totalCrimeIndex) ? Number(data.totalCrimeIndex) : 50;
  const propertyIdx = Number.isFinite(data.propertyCrimeIndex) ? Number(data.propertyCrimeIndex) : 50;
  const stateTotalIdx = Number.isFinite(data.stateIndices?.totalCrimeIndex)
    ? Number(data.stateIndices.totalCrimeIndex)
    : totalIdx;
  const statePropertyIdx = Number.isFinite(data.stateIndices?.propertyCrimeIndex)
    ? Number(data.stateIndices.propertyCrimeIndex)
    : propertyIdx;
  const metroTotalIdx = Number.isFinite(data.metroIndices?.totalCrimeIndex)
    ? Number(data.metroIndices.totalCrimeIndex)
    : stateTotalIdx;
  const metroPropertyIdx = Number.isFinite(data.metroIndices?.propertyCrimeIndex)
    ? Number(data.metroIndices.propertyCrimeIndex)
    : statePropertyIdx;

  const gaugeColors = ['#22c55e', '#eab308', '#ef4444'];

  const buildTrendSeries = (trend: any) => {
    if (!trend?.years || !trend?.datasets) return [];
    return trend.years.map((year: number | string, index: number) => {
      const point: any = { year };
      const values: number[] = [];
      for (const ds of trend.datasets) {
        const value = Number(ds.data[index]);
        point[ds.label] = ds.data[index];
        if (Number.isFinite(value)) values.push(value);
      }
      if (values.length > 0) {
        point[TOTAL_CRIME_TREND_LABEL] = values.reduce((sum, value) => sum + value, 0);
      }
      return point;
    });
  };

  const crimeTrend = data.propertyCrimeTrend || data.crimeTrend;
  const propertyCrimeTrendSeries = buildTrendSeries(crimeTrend);

  const renderGauge = (label: string, value: number | null | undefined) => {
    if (value == null) return null;
    const clamped = Math.max(0, Math.min(100, value));

    const bands = [
      { name: 'Low', value: 33.34 },
      { name: 'Moderate', value: 33.33 },
      { name: 'High', value: 33.33 },
    ];

    const needleAngle = (clamped / 100) * 180 - 90; // -90 to +90

    return (
      <div className="card" style={{ padding: '12px 14px', textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>{clamped}</div>
        <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>{label}</div>
        <div style={{ position: 'relative', height: 120 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={bands}
                dataKey="value"
                startAngle={180}
                endAngle={0}
                innerRadius={40}
                outerRadius={60}
                stroke="none"
              >
                {bands.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={gaugeColors[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 54,
              width: 12,
              height: 12,
              borderRadius: '999px',
              backgroundColor: '#111827',
              transform: 'translateX(-50%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 60,
              width: 2,
              height: 46,
              backgroundColor: '#111827',
              transformOrigin: '50% 100%',
              transform: `translateX(-50%) rotate(${needleAngle}deg)`,
            }}
          />
          <div style={{ position: 'absolute', left: 12, bottom: 0, fontSize: 10, color: '#6b7280' }}>0</div>
          <div style={{ position: 'absolute', right: 12, bottom: 0, fontSize: 10, color: '#6b7280' }}>100</div>
        </div>
      </div>
    );
  };

  const toIndex = (raw: any, denominator: number, fallback: number) => {
    const n = Number(raw);
    // Per-100k rates from FBI CDE: map onto 0–100 using offense-specific national denoms.
    if (Number.isFinite(n) && n >= 0) {
      return Math.max(0, Math.min(100, Math.round((n / denominator) * 50)));
    }
    return fallback;
  };

  const makePair = (
    id: string,
    label: string,
    value: number,
    metroValue: number,
    stateValue: number,
  ) => ({
    id,
    label,
    value,
    metro: Math.max(0, Math.min(100, Math.round(metroValue))),
    state: Math.max(0, Math.min(100, Math.round(stateValue))),
  });

  const personalCrimeCards = [
    makePair(
      'murder',
      'Murder Index',
      toIndex(cp.murder ?? cd.murder, 6, Math.round(totalIdx * 0.7)),
      Math.round(metroTotalIdx * 0.7),
      toIndex(stateCp.murder, 6, Math.round(stateTotalIdx * 0.7)),
    ),
    makePair(
      'rape',
      'Rape Index',
      toIndex(cp.rape, 40, Math.round(totalIdx * 0.6)),
      Math.round(metroTotalIdx * 0.6),
      toIndex(stateCp.rape, 40, Math.round(stateTotalIdx * 0.6)),
    ),
    makePair(
      'robbery',
      'Robbery Index',
      toIndex(cp.robbery, 80, Math.round(totalIdx * 0.7)),
      Math.round(metroTotalIdx * 0.7),
      toIndex(stateCp.robbery, 80, Math.round(stateTotalIdx * 0.7)),
    ),
    makePair(
      'assault',
      'Assault Index',
      toIndex(cp.assault, 250, Math.round(totalIdx * 0.9)),
      Math.round(metroTotalIdx * 0.9),
      toIndex(stateCp.assault, 250, Math.round(stateTotalIdx * 0.9)),
    ),
  ];

  const propertyCrimeCards = [
    makePair(
      'burglary',
      'Burglary Index',
      toIndex(cp.burglary, 340, Math.round(propertyIdx * 1.0)),
      Math.round(metroPropertyIdx),
      toIndex(stateCp.burglary, 340, Math.round(statePropertyIdx)),
    ),
    makePair(
      'larceny',
      'Larceny Index',
      toIndex(cp.larceny, 1400, Math.round(propertyIdx * 1.0)),
      Math.round(metroPropertyIdx),
      toIndex(stateCp.larceny, 1400, Math.round(statePropertyIdx)),
    ),
    makePair(
      'vehicle',
      'Motor Vehicle Crime Index',
      toIndex(cp.vehicleTheft, 280, Math.round(propertyIdx)),
      Math.round(metroPropertyIdx),
      toIndex(stateCp.vehicleTheft, 280, Math.round(statePropertyIdx)),
    ),
  ];

  const totalCrimeCard = makePair('total', 'Total Crime Index', totalIdx, metroTotalIdx, stateTotalIdx);

  const renderRiskBar = (label: string, value: number, metro: number, state: number) => {
    const yMax = Math.max(25, value, metro, state, 100);
    return (
      <div className="card" style={{ padding: '12px 14px' }}>
        <h3 style={{ marginBottom: 8, fontSize: 12 }}>{label}</h3>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart
            data={[
              { name: neighborhoodLabel, value },
              { name: metroLabel, value: metro },
            ]}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
            <YAxis
              domain={[0, yMax]}
              ticks={[0, 25, 50, 75, 100].filter((t) => t <= yMax)}
              tick={{ fontSize: 11 }}
            />
            <Tooltip />
            <ReferenceLine
              y={state}
              stroke="#f44336"
              strokeDasharray="4 2"
              label={{
                value: `State: ${state}`,
                position: 'insideTopRight',
                fill: '#f44336',
                fontSize: 11,
              }}
            />
            <Bar dataKey="value">
              <Cell fill="#e53935" />
              <Cell fill="#42a5f5" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div>
      <h2>C - CRIME (Crime & Safety)</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        How risky is this neighborhood from a safety standpoint?
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        <div className="card">
          <h3>Safety Score</h3>
          <p style={{ fontSize: '48px', fontWeight: 'bold', color: safetyColor }}>
            {data.safetyScore != null ? data.safetyScore : 'N/A'}
          </p>
          <p style={{ fontSize: '12px', color: '#666' }}>Out of 100 (higher is safer)</p>
        </div>
        <div className="card">
          <h3>Total Crime Index</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {data.totalCrimeIndex != null ? data.totalCrimeIndex : 'N/A'}
          </p>
        </div>
        <div className="card">
          <h3>Property Crime Index</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {data.propertyCrimeIndex != null ? data.propertyCrimeIndex : 'N/A'}
          </p>
        </div>
      </div>

      <h3 style={{ marginBottom: 8 }}>Personal Crime</h3>
      {personalCrimeCards.map((item) => (
        <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 1fr', gap: 12, marginBottom: 10 }}>
          {renderGauge(item.label, item.value)}
          {renderRiskBar(`Risk of ${item.label.replace(' Index', '')} relative to Metro Area`, item.value, item.metro, item.state)}
        </div>
      ))}

      <h3 style={{ margin: '16px 0 8px' }}>Property Crime</h3>
      {propertyCrimeCards.map((item) => (
        <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 1fr', gap: 12, marginBottom: 10 }}>
          {renderGauge(item.label, item.value)}
          {renderRiskBar(`Risk of ${item.label.replace(' Index', '')} relative to Metro Area`, item.value, item.metro, item.state)}
        </div>
      ))}

      <h3 style={{ margin: '16px 0 8px' }}>Total Crime</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 1fr', gap: 12, marginBottom: 14 }}>
        {renderGauge(totalCrimeCard.label, totalCrimeCard.value)}
        {renderRiskBar('Risk of crime relative to Metro Area', totalCrimeCard.value, totalCrimeCard.metro, totalCrimeCard.state)}
      </div>

      <div className="card">
        <h3>Crime Trend for the Neighborhood for the last 10 years</h3>
        {propertyCrimeTrendSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={propertyCrimeTrendSeries}
              margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="year"
                interval={0}
                tick={{ fontSize: 11 }}
              />
              <YAxis />
              <Tooltip />
              {crimeTrend?.datasets?.map((ds: any, index: number) => {
                const color = ['#0ea5e9', '#22c55e', '#f97316', '#6366f1', '#e11d48', '#a855f7'][index % 6];
                return (
                  <Line
                    key={ds.label}
                    type="monotone"
                    dataKey={ds.label}
                    stroke={color}
                    strokeWidth={3}
                    dot={{ r: 4, fill: color, strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: color }}
                  />
                );
              })}
              <Line
                type="monotone"
                dataKey={TOTAL_CRIME_TREND_LABEL}
                stroke="#a855f7"
                strokeWidth={3}
                dot={{ r: 4, fill: '#a855f7', strokeWidth: 0 }}
                activeDot={{ r: 6, fill: '#a855f7' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 14, margin: '16px 0 0' }}>
            Trend not available for this area. For state-level trend (per 100k), add <strong>FBI_API_KEY</strong> or <strong>DATA_GOV_API_KEY</strong> in backend <code>.env</code>. For Chicago-area addresses, trend is filled from city open data when FBI key is not set.
          </p>
        )}
      </div>

      {missingFields.length > 0 && (
        <RequiredApiNotice apis={['FBI Crime Data API', 'City Open Data Crime APIs']} missingFields={missingFields} />
      )}
    </div>
  );
};

export default CrimeDashboard;
